import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Menu, X, Volume2, VolumeX } from "lucide-react";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";

export const DashLayout = ({ nav, title, subtitle, actions, children }) => {
  const { user, logout, soundOn, toggleSound } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#F4F7FB]">
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-[#0A1F44] transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center justify-between px-5">
          <Brand dark />
          <button className="text-slate-300 lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close-btn"><X className="h-5 w-5" /></button>
        </div>
        <nav className="mt-4 flex-1 space-y-1 px-3">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end
              onClick={() => setOpen(false)}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#2563EB] text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"}`
              }
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <div className="truncate text-sm font-semibold text-white" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="truncate text-xs text-slate-400">{user?.email}</div>
          <button onClick={doLogout} data-testid="logout-btn" className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-white/15 py-2 text-sm text-slate-200 transition-colors hover:bg-white/10">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/85 px-4 backdrop-blur-xl sm:px-8">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open-btn"><Menu className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-display text-lg font-bold text-[#0A1F44]" data-testid="page-title">{title}</h1>
            {subtitle && <p className="hidden truncate text-xs text-slate-500 sm:block">{subtitle}</p>}
          </div>
          {actions}
          {user?.role === "user" && (
            <div className="flex items-center gap-2">
              <span className="hidden items-center gap-1.5 text-xs font-semibold text-emerald-600 sm:flex" data-testid="live-indicator">
                <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
                Live
              </span>
              <button onClick={toggleSound} data-testid="sound-toggle-btn" title={soundOn ? "Payment sound on" : "Payment sound off"}
                className={`grid h-9 w-9 place-items-center rounded-lg border transition-colors ${soundOn ? "border-blue-200 bg-blue-50 text-[#2563EB]" : "border-slate-200 text-slate-400 hover:bg-slate-50"}`}>
                {soundOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </button>
            </div>
          )}
        </header>
        <main className="mx-auto min-w-0 max-w-7xl space-y-6 p-4 sm:p-8">{children}</main>
      </div>
    </div>
  );
};
